import * as React from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useForceUpdate } from './useForceUpdate';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';
const { createContext, forwardRef, useCallback, useContext, useImperativeHandle, useMemo } = React;
const Ref = forwardRef(function Ref(_props, ref) {
    useImperativeHandle(ref, ()=>({}), []);
    return null;
});
const delimiter = '___';
function getKeys(callback) {
    let keys = '';
    const result = callback((key)=>{
        keys = keys ? `${keys}${delimiter}${key}` : key;
    });
    return [
        result,
        keys
    ];
}
export function createContextTree({ assign, broadcast, create, keys: toKeys = Object.keys, useValue = (v)=>v }) {
    const ListenerContext = createContext({});
    const ValueContext = createContext({
        status: 'committed',
        committed: create(),
        staged: create(),
        onUpdate: new Set(),
        checkNeedsUpdate () {
            return false;
        }
    });
    function useContextValue() {
        const context = useContext(ValueContext);
        const forceUpdate = useForceUpdate();
        useIsomorphicLayoutEffect(()=>{
            context.onUpdate.add(forceUpdate);
            return ()=>{
                context.onUpdate.delete(forceUpdate);
            };
        }, []);
        const isStaged = context.status === 'staged';
        return isStaged ? context.staged : context.committed;
    }
    function useSelector(selector) {
        const listenerMap = useContext(ListenerContext);
        const forceUpdate = useForceUpdate();
        const context = useContext(ValueContext);
        const isStaged = context.status === 'staged';
        const [value, keys] = getKeys((subscribe)=>isStaged ? selector(context.staged, subscribe) : selector(context.committed, subscribe));
        useIsomorphicLayoutEffect(()=>{
            if (context.checkNeedsUpdate()) {
                forceUpdate();
                return;
            }
            const callback = ()=>{
                const [committedValue, committedKeys] = getKeys((subscribe)=>selector(context.committed, subscribe));
                if (!Object.is(value, committedValue) || keys !== committedKeys) {
                    forceUpdate();
                }
            };
            const keyList = keys.split(delimiter);
            keyList.forEach((key)=>{
                var _listenerMap, _key;
                var _;
                (_ = (_listenerMap = listenerMap)[_key = key]) !== null && _ !== void 0 ? _ : _listenerMap[_key] = new Set();
                listenerMap[key].add(callback);
            });
            return ()=>{
                keyList.forEach((key)=>{
                    var _listenerMap_key;
                    (_listenerMap_key = listenerMap[key]) === null || _listenerMap_key === void 0 ? void 0 : _listenerMap_key.delete(callback);
                });
            };
        }, [
            context,
            forceUpdate,
            keys,
            listenerMap,
            selector,
            value
        ]);
        return value;
    }
    function Provider({ children, value: valueProp }) {
        const value = useValue(valueProp);
        const forceUpdate = useForceUpdate();
        const listenerMap = useContext(ListenerContext);
        const inherited = useContext(ValueContext);
        const context = useMemo(()=>({
                staged: create(inherited.staged),
                committed: create(inherited.committed),
                parent: inherited,
                status: inherited.status,
                onUpdate: new Set(),
                checkNeedsUpdate () {
                    // eslint-disable-next-line react/no-this-in-sfc
                    if (this.status === 'committed') {
                        return false;
                    }
                    forceUpdate();
                    return true;
                }
            }), [
            forceUpdate,
            inherited
        ]);
        useIsomorphicLayoutEffect(()=>{
            const propagate = ()=>context.onUpdate.forEach((listener)=>listener());
            inherited.onUpdate.add(propagate);
            return ()=>{
                inherited.onUpdate.delete(propagate);
            };
        }, [
            forceUpdate,
            inherited
        ]);
        const keys = useMemo(()=>Array.from(new Set([
                ...toKeys(context.committed),
                ...toKeys(value)
            ])), [
            value,
            context
        ]);
        useMemo(()=>{
            assign(context.staged, value, keys);
            context.status = 'staged';
        }, [
            value,
            context,
            keys
        ]);
        const commit = useCallback(// eslint-disable-next-line @typescript-eslint/no-explicit-any
        (ref)=>{
            // The callback will be called with an object when the component is
            // mounted and null when the component is unmounted.
            if (!ref) {
                return;
            }
            batchedUpdates(()=>{
                var _context_parent;
                if ((_context_parent = context.parent) === null || _context_parent === void 0 ? void 0 : _context_parent.checkNeedsUpdate()) {
                    forceUpdate();
                    return;
                }
                const changedKeys = broadcast(context.committed, value, keys);
                if (changedKeys.length) {
                    context.onUpdate.forEach((listener)=>listener());
                }
                assign(context.committed, value, keys);
                changedKeys.forEach((key)=>{
                    var _listenerMap_key;
                    return (_listenerMap_key = listenerMap[key]) === null || _listenerMap_key === void 0 ? void 0 : _listenerMap_key.forEach((listener)=>listener());
                });
                context.status = 'committed';
            });
        }, [
            value,
            context,
            keys,
            forceUpdate,
            listenerMap
        ]);
        return /*#__PURE__*/ React.createElement(ValueContext.Provider, {
            value: context
        }, /*#__PURE__*/ React.createElement(Ref, {
            ref: commit
        }), children);
    }
    return {
        useContextValue,
        useSelector,
        Context: {
            Provider
        }
    };
}

//# sourceMappingURL=createContextTree.js.map